import { ApolloError } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { useCallback, useEffect, useMemo, useRef } from "react";
import Swal from "sweetalert2";
import {
  CreatePartyInput,
  useCreatePartyMutation,
  useDeletePartiesWithIdsMutation,
  useEditPartyByIdMutation,
  useGetLatestPartyLazyQuery,
  useGetPartyWithIdLazyQuery,
} from "../../generated/graphql";
import { NumberFormatCustom } from "../../utils/CustomNumberInput";
import { sweetAlert } from "../../utils/sweetAlert";
import { PartyValidation } from "../../validation/partyValidation";
import { useAppSelector } from "../../hooks/usereduxHooks";
import { useNavigate, useParams } from "react-router";

interface ExtendedCreatePartyInput extends CreatePartyInput {
  id?: number;
}

export interface AddPartyModalProps {
  isModal?: boolean;
  onSuccess?: (createdParty: any) => void;
  onClose?: () => void;
  initialPartyCode?: string;
}

export default function AddPartyModal({
  isModal = false,
  onSuccess,
  onClose,
  initialPartyCode,
}: AddPartyModalProps = {}) {
  const {
    user: { id: userId },
  } = useAppSelector((store) => store.User);

  const { partyId: routePartyId } = useParams();
  const partyId = initialPartyCode || routePartyId;

  const initialValue = useMemo(
    () => ({
      partyCode: partyId ?? "",
      address: "",
      city: "",
      email: "",
      mobileNumber: "",
      phoneNumber: "",
      partyName: "",
      company: "",
      openingBalance: 0,
      debitBalance: 0,
      creditBalance: 0,

      userId,
    }),
    [partyId, userId],
  );

  const formik = useFormik({
    enableReinitialize: true,
    onSubmit: async (values: ExtendedCreatePartyInput, { resetForm }) => {
      if (!values.id) {
        await createParty({
          variables: {
            createPartyInput: {
              ...values,
              partyCode: values.partyCode.trim(),

              openingBalance: parseFloat(`${values.openingBalance}`),
              debitBalance: parseFloat(`${values.debitBalance}`),
              creditBalance: parseFloat(`${values.creditBalance}`),
            },
          },
        });
      } else {
        const {
          partyCode,
          address,
          city,
          email,
          mobileNumber,
          phoneNumber,
          partyName,
          company,
          userId,
        } = values;
        await editParty({
          variables: {
            editPartyInput: {
              id: values.id,
              partyCode: partyCode.trim(),
              userId,
              address,
              city,
              email,
              mobileNumber,
              phoneNumber,
              partyName,
              company,
              openingBalance: parseFloat(`${values.openingBalance}`),
              debitBalance: parseFloat(`${values.debitBalance}`),
              creditBalance: parseFloat(`${values.creditBalance}`),
            },
          },
        });
      }
    },
    initialValues: {
      ...initialValue,
    },
    validationSchema: PartyValidation,
  });

  const {
    handleBlur,
    resetForm,
    errors,
    handleChange,
    touched,
    values,
    setValues,
    setFieldValue,
    handleSubmit,
  } = formik;

  const [deletePartyWithIds, { loading: loadingDelete }] =
    useDeletePartiesWithIdsMutation({
      onCompleted: (data) => {
        sweetAlert("Party has been deleted", "success");
        resetForm();
        getLatestParty();
      },
      onError: (err: ApolloError) => {
        sweetAlert(err.message, "error");
      },
    });

  const [editParty, { loading: loadingPartyUpdate }] =
    useEditPartyByIdMutation({
      onCompleted: (data) => {
        if (isModal && onSuccess) {
          sweetAlert("Party updated", "success").then(() => {
            onSuccess(data?.editPartyById || values);
            onClose?.();
          });
          return;
        }
        sweetAlert("Party updated", "success").then(() => {
          resetForm();
          navigate("/accounts/add-party");
          setTimeout(() => {
            inputRef?.current?.focus();
          }, 500);
        });
      },
      onError: (err: ApolloError) => {
        sweetAlert(err.message, "error");
      },
    });

  const [createParty, { loading }] = useCreatePartyMutation({
    onCompleted: (data) => {
      if (isModal && onSuccess) {
        sweetAlert("Party created", "success").then(() => {
          onSuccess(data?.createParty || values);
          onClose?.();
        });
        return;
      }
      sweetAlert("Party created", "success").then(() => {
        resetForm();
        getLatestParty();
        setTimeout(() => {
          inputRef?.current?.focus();
        }, 500);
      });
    },
    onError: (err: ApolloError) => {
      sweetAlert(err.message, "error");
    },
  });

  const [getPartyMutate] = useGetPartyWithIdLazyQuery();
  const [getQuery] = useGetLatestPartyLazyQuery();

  const onPartyCodeBlur = useCallback(
    async (codeToFind?: string) => {
      const targetCode = (codeToFind ?? values.partyCode)?.trim();
      if (!targetCode) return;
      const party = await getPartyMutate({
        variables: {
          id: targetCode,
        },
        fetchPolicy: "no-cache",
      });
      if (party.data?.getPartyWithId) {
        setValues({
          ...party.data?.getPartyWithId,
          openingBalance: Number(
            party.data?.getPartyWithId.openingBalance?.toFixed(2),
          ),
          userId,
        } as ExtendedCreatePartyInput);
      } else if (!partyId) {
        setFieldValue("id", undefined);
      }
    },
    [getPartyMutate, partyId, setFieldValue, setValues, userId, values.partyCode],
  );

  const getLatestParty = useCallback(async () => {
    const { data } = await getQuery({
      fetchPolicy: "no-cache",
    });

    let partyCode = "XXX 01";

    if (data?.getLatestParty.partyCode) {
      let numberPart = !isNaN(
        Number(data?.getLatestParty?.partyCode.replace(/^\D+/g, "")),
      )
        ? Number(data?.getLatestParty?.partyCode.replace(/^\D+/g, "")) + 1
        : "001";
      let stringPart = data?.getLatestParty.partyCode.replace(/[\d\s]/g, "");
      partyCode = `${stringPart} ${numberPart.toString().padStart(2, "0")}`;
    }

    resetForm({});
    setValues({
      ...initialValue,
      partyCode: initialPartyCode || partyCode,
    });
  }, [getQuery, initialPartyCode, initialValue, resetForm, setValues]);

  useEffect(() => {
    if (!partyId) {
      getLatestParty();
    } else {
      onPartyCodeBlur(partyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId, initialPartyCode]);

  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Grid
      item
      xs={12}
      md={12}
      sm={12}
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: isModal ? "100%" : "960px",
        mx: isModal ? 0 : "auto",
        width: "100%",
      }}
    >
      {!isModal && (
        <Box
          display="flex"
          alignItems={{ xs: "stretch", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          sx={{ marginBottom: 4 }}
        >
          <Box display="flex" alignItems="center" justifyContent={"flex-start"}>
            <Box
              display="flex"
              alignItems="flex-start"
              flexDirection={"column"}
              justifyContent={"flex-start"}
              marginBottom={{ xs: "20px", md: "0px" }}
            >
              <Typography variant="h3" component="h3" gutterBottom>
                {"Accounts"}
              </Typography>
              <Typography variant="subtitle2">Add Account</Typography>
            </Box>
          </Box>
        </Box>
      )}
      <FormikProvider value={formik}>
        <Grid
          columnSpacing={{ xs: 1, sm: 1, md: 1 }}
          container
          alignItems={"center"}
        >
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              required
              fullWidth
              placeholder="Party code"
              label="Party Code"
              id="partyCode"
              size="small"
              value={values.partyCode}
              aria-required
              inputProps={{
                maxLength: 12,
                style: { textTransform: "uppercase" },
              }}
              name="partyCode"
              onChange={handleChange}
              onBlur={(e) => {
                handleBlur(e);
                onPartyCodeBlur();
              }}
              autoComplete="partyCode"
              error={Boolean(touched.partyCode && errors.partyCode)}
              helperText={touched.partyCode ? errors.partyCode : ""}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["partyCode"] &&
                (touched.partyCode ? errors.partyCode : "")}
            </FormHelperText>
          </Grid>
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              required
              autoFocus
              fullWidth
              inputRef={inputRef}
              placeholder="Party Name"
              label="Party Name"
              size="small"
              value={values.partyName}
              aria-required
              inputProps={{
                maxLength: 40,
                style: { textTransform: "uppercase" },
              }}
              name="partyName"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.partyName && errors.partyName)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["partyName"] &&
                (touched.partyName ? errors.partyName : "")}
            </FormHelperText>
          </Grid>

          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              placeholder="Address"
              label="Address"
              size="small"
              value={values.address}
              name="address"
              inputProps={{
                maxLength: 40,
                style: { textTransform: "uppercase" },
              }}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.address && errors.address)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["address"] && (touched.address ? errors.address : "")}
            </FormHelperText>
          </Grid>

          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              placeholder="City"
              label="City"
              inputProps={{
                maxLength: 40,
                style: { textTransform: "uppercase" },
              }}
              size="small"
              value={values.city}
              name="city"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.city && errors.city)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["city"] && (touched.city ? errors.city : "")}
            </FormHelperText>
          </Grid>
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              size="small"
              placeholder="Forward Balance"
              label="Forward Credit Balance"
              value={`${values?.openingBalance}`}
              // disabled={Boolean(
              //   partyFound?.getPartyWithId?.timestamps?.createdAt &&
              //     dayjs(
              //       Number(partyFound?.getPartyWithId?.timestamps?.createdAt),
              //     ).toDate() < dayjs().subtract(30, "D").toDate() &&
              //     userId !== 14,
              // )}
              name="openingBalance"
              onChange={(e) => {
                handleChange(e);
              }}
              InputProps={{
                inputComponent: NumberFormatCustom as any,
              }}
              onBlur={handleBlur}
              error={Boolean(touched.openingBalance && errors.openingBalance)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["openingBalance"] &&
                (touched.openingBalance ? errors.openingBalance : "")}
            </FormHelperText>
          </Grid>
          {/* <Grid item md={6} sm={12} xs={12}>
              <TextField
                margin="normal"
                fullWidth
                placeholder="debit Balance"
                label="Debit Balance"
                value={`${values?.debitBalance}`}
                name="debitBalance"
                onChange={(e) => {
                  handleChange(e);
                }}
                InputProps={{
                  inputComponent: NumberFormatCustom as any,
                }}
                onBlur={handleBlur}
                error={Boolean(touched.balance && errors.balance)}
                helperText={touched.balance ? errors.balance : ""}
              />
            </Grid>
            <Grid item md={6} sm={12} xs={12}>
              <TextField
                margin="normal"
                fullWidth
                placeholder="Credit Balance"
                label="Credit Balance"
                value={`${values?.creditBalance}`}
                name="creditBalance"
                onChange={(e) => {
                  handleChange(e);
                }}
                InputProps={{
                  inputComponent: NumberFormatCustom as any,
                }}
                onBlur={handleBlur}
                error={Boolean(touched.balance && errors.balance)}
                helperText={touched.balance ? errors.balance : ""}
              />
            </Grid> */}
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              placeholder="Email"
              label="Email"
              value={values.email}
              name="email"
              inputProps={{
                maxLength: 20,
              }}
              size="small"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.email && errors.email)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["email"] && (touched.email ? errors.email : "")}
            </FormHelperText>
          </Grid>
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              placeholder="Phone number"
              label="Phone number"
              value={values.phoneNumber}
              name="phoneNumber"
              inputProps={{
                maxLength: 13,
              }}
              size="small"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.phoneNumber && errors.phoneNumber)}
            />
            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["phoneNumber"] &&
                (touched.phoneNumber ? errors.phoneNumber : "")}
            </FormHelperText>
          </Grid>
          <Grid item md={6} sm={12} xs={12}>
            <TextField
              margin="normal"
              fullWidth
              placeholder="Mobile Number"
              label="Mobile Number"
              value={values.mobileNumber}
              name="mobileNumber"
              inputProps={{
                maxLength: 13,
              }}
              size="small"
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.mobileNumber && errors.mobileNumber)}
            />

            <FormHelperText sx={{ color: "error.main", height: "20px" }}>
              {errors["mobileNumber"] &&
                (touched.mobileNumber ? errors.mobileNumber : "")}
            </FormHelperText>
          </Grid>
        </Grid>

        {isModal ? (
          <Grid
            container
            spacing={2}
            justifyContent="flex-end"
            sx={{ mt: 2, mb: 1 }}
          >
            {onClose && (
              <Grid item xs={6} sm={4}>
                <LoadingButton
                  fullWidth
                  variant="outlined"
                  color="inherit"
                  onClick={onClose}
                >
                  Cancel
                </LoadingButton>
              </Grid>
            )}
            <Grid item xs={onClose ? 6 : 12} sm={4}>
              <LoadingButton
                loading={loading || loadingPartyUpdate}
                type="submit"
                fullWidth
                variant="contained"
              >
                {values.id ? "Update Party" : "Create Party"}
              </LoadingButton>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={3}
            flexDirection="row"
            justifyContent={"center"}
          >
            <Grid item md={!values.id ? 12 : 4} sm={12}>
              <LoadingButton
                loading={loading || loadingPartyUpdate}
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                {values.id ? "Update Party" : "Create Party"}
              </LoadingButton>
            </Grid>
            {values.id ? (
              <>
                <Grid item md={4} sm={12}>
                  <LoadingButton
                    loading={loadingDelete}
                    type="button"
                    onClick={async () => {
                      const id = values.id as number;
                      Swal.fire({
                        title: "Do you really want to Delete?",
                        showDenyButton: true,
                        showCancelButton: true,
                        confirmButtonText: "Delete",
                        denyButtonText: `Don't Delete`,
                      }).then(async (result) => {
                        /* Read more about isConfirmed, isDenied below */
                        if (result.isConfirmed) {
                          await deletePartyWithIds({
                            variables: {
                              deletewithids: {
                                id: [id],
                              },
                            },
                          });
                          setValues({
                            partyCode: "",
                            address: "",
                            city: "",
                            email: "",
                            mobileNumber: "",
                            phoneNumber: "",
                            partyName: "",
                            company: "",
                            openingBalance: 0,

                            userId,
                          });

                          navigate("/accounts/add-party");
                          return sweetAlert("Deleted", "success");
                        }
                      });
                    }}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Delete Party
                  </LoadingButton>
                </Grid>

                <Grid item md={values.id ? 4 : 6} sm={12}>
                  <LoadingButton
                    loading={loading || loadingPartyUpdate}
                    onClick={() => {
                      navigate("/accounts/add-party");

                      getLatestParty();
                    }}
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Add New
                  </LoadingButton>
                </Grid>
              </>
            ) : null}
          </Grid>
        )}
      </FormikProvider>
    </Grid>
  );
}
